<template>
	<div class="Suggest" :class="classes">
		<label :for="id" class="Suggest-label" :id="'Suggest-label-' + id">{{ label }}</label>
		<div
			class="Suggest-container"
			:class="{ isFocused: showFocus }"
			role="combobox"
			:aria-controls="'Suggest-dropdown-' + id"
			:aria-labelledby="'Suggest-label-' + id"
			:aria-expanded="open ? 'true' : 'false'"
		>
			<div class="Suggest-icon">
				<CloIcon symbol="search" />
			</div>
			<div class="Suggest-inputContainer">
				<input
					type="text"
					class="Suggest-input"
					:value="inputValue"
					:name="name"
					:id="id"
					:placeholder="placeholder"
					ref="input"
					:dir="layoutDirection"
					@input="handleTyping"
					@click="handleTyping"
					@keydown="handleKeydown"
					@focus="showFocus = true"
					@blur="showFocus = false"
					autocomplete="off"
					data-lpignore="true"
					spellcheck="false"
					aria-autocomplete="list"
					:aria-controls="open ? 'Suggest-dropdown-' + id : false"
					:aria-owns="open ? 'Suggest-dropdown-' + id : false"
					:aria-activedescendent="open ? 'Suggest-suggestion-' + selectionIndex : false"
					:aria-labelledby="'Suggest-label-' + id"
				/>
			</div>
			<div class="Suggest-spinner" v-if="isSearching">
				<CloIcon symbol="spinner" />
			</div>
			<div class="Suggest-reset" v-if="inputValue && !isSearching">
				<button type="button" @click="resetValue">
					<CloIcon symbol="close" />
					<span class="is-visuallyHidden">{{ $t('generic.Clear') }}</span>
				</button>
			</div>
			<div class="Suggest-submit" v-if="submit">
				<button type="submit" class="Button">{{ submit }}</button>
			</div>
			<div
				v-show="open"
				class="Suggest-dropdown"
				role="listbox"
				:id="'Suggest-dropdown-' + id"
				:style="{ maxHeight: dropdownMaxHeight }"
			>
				<div class="Suggest-suggestions">
					<div v-for="(item, index) in suggestions" :key="index">
						<div
							role="option"
							class="Suggest-suggestion"
							:class="{
								'Suggest-cursor': selectionIndex === index,
							}"
							:id="'Suggest-suggestion-' + index"
							@click="selected(item, index)"
						>
							<span class="Suggestion-text" v-html="item.highlightedName"></span>
						</div>
					</div>
					<div class="Suggest-empty" :class="{ 'Suggest-emptyCreate': createOnEmptyMsg }" v-if="!suggestions.length">
						<a v-if="createOnEmptyMsg">{{ createOnEmptyMsg }}</a>
						<span v-else>{{ $t('generic.NoMatches') }}</span>
					</div>
					<div v-if="locations === 'place' || locations === 'cities'" class="Suggest-datasetFooter">
						<img :src="poweredByGoogle" alt="powered by Google" />
					</div>
				</div>
			</div>
		</div>
		<div :class="a11yStatusClasses" v-if="inputValue.length && open">
			<div role="status" aria-atomic="true" aria-live="polite">{{ status.count }} {{ $t('search.matchesAvailable') }}</div>
			<div role="status" aria-atomic="true" aria-live="polite" v-if="suggestions.length">
				{{ $t('search.useArrows') }}
			</div>
			<div role="status" aria-atomic="true" aria-live="assertive">
				{{ status.suggestion }}
			</div>
		</div>
	</div>
</template>

<script>
import axios from 'axios';
import CloIcon from '@/icons/CloIcon/CloIcon.vue';
import CloLoadingAnimation from '@/base/CloLoadingAnimation/CloLoadingAnimation.vue';

export default {
	name: 'CloSuggest',
	components: { CloIcon, CloLoadingAnimation },
	props: {
		/**
		 * REQUIRED: use prop hideLabel to hide it while keeping it accessible
		 */
		label: {
			type: String,
			required: true,
		},
		/**
		 * sm or lg
		 */
		size: {
			type: String,
			default: null,
		},
		/**
		 * visually hides the label
		 */
		hideLabel: {
			type: Boolean,
			default: false,
		},
		/**
		 * visually hides the label
		 */
		pill: {
			type: Boolean,
			default: false,
		},
		/**
		 * visually hides the label
		 */
		reverse: {
			type: Boolean,
			default: false,
		},
		/**
		 * Reminder of what kind of text to enter in the input
		 */
		placeholder: {
			type: String,
			default: '',
		},
		/**
		 * API url with %QUERY to be replaced by what the user types
		 */
		url: {
			type: String,
			default: '',
		},
		/**
		 * unique id
		 */
		id: {
			type: String,
			default: 'id-' + Math.random().toString(36).substr(2, 9),
		},
		/**
		 * name for use in submitting forms
		 */
		name: {
			type: String,
			default: '',
		},
		/**
		 * initial value of input
		 */
		value: {
			type: String,
			default: '',
		},
		/**
		 * only needed when locations=place or locations=city
		 */
		googleApiKey: {
			type: String,
			default: '',
		},
		/**
		 * google places config, can be places or cities (cities limits results to only cities)
		 */
		locations: {
			type: String,
			default: '',
		},
		/**
		 * characters to type before making request to API
		 */
		minLength: {
			type: Number,
			default: 3,
		},
		/**
		 * show submit button
		 */
		submit: {
			type: String,
			default: '',
		},
		/**
		 * array of codes - results will include ONLY codes in this list
		 */
		filter: {
			type: Array,
			default() {
				return [];
			},
		},
		/**
		 * array of codes - results will EXCLUDE codes in this list
		 */
		filterExclude: {
			type: Array,
			default() {
				return [];
			},
		},
		/**
		 * automatically navigate to a URL - %CODE and %NAME will be replaced with the chosen result
		 */
		goTo: {
			type: String,
			default: '',
		},
		/**
		 * display a11y aria-live content
		 */
		debug: {
			type: Boolean,
			default: false,
		},
		/**
		 * TODO: deprecate
		 */
		test: {
			type: Boolean,
			default: false,
		},
		/**
		 * not sure - deprecate?
		 */
		createOnEmptyMsg: {
			type: String,
			default: '',
		},
		/**
		 * use a local list instead of url of API
		 */
		localList: {
			type: Array,
			default() {
				return [];
			},
		},
		/**
		 * clear the input value when selection is made
		 */
		clearOnSelect: {
			type: Boolean,
			default: false,
		},
		/**
		 * adjust debounce time for search
		 */
		debounceTime: {
			type: Number,
			default: 200,
		},
	},
	watch: {
		value(newValue) {
			this.inputValue = newValue;
		},
	},
	data() {
		return {
			open: false,
			cache: {},
			suggestions: [],
			requestTimer: false,
			dropdownMaxHeight: 'auto',
			dropdownWidth: 'auto',
			inputValue: '',
			isSearching: false,
			showFocus: false,
			mightCancel: false,
			selectionIndex: 0,
			status: {
				suggestion: '',
				count: '',
			},
			googleMapsApiLoaded: false,
			regionParts: ['country', 'state', 'county'],
			regexEscape: (RegExp.escape = function (s) {
				return s.replace(/[-\/\\^$*+?.()|[\]{}]/g, '\\$&');
			}),
			analyticsCategory: 'typeahead',
			species: false,
			cancelToken: null,
			layoutDirection: 'auto',
		};
	},
	created() {
		this.inputValue = this.value;
	},
	computed: {
		classes() {
			return {
				'Suggest--reverse': this.reverse,
				'Suggest--pill': this.pill,
				'Suggest--hideLabel': this.hideLabel,
				'Suggest--sm': this.size === 'sm',
				'Suggest--lg': this.size === 'lg',
			};
		},
		resultsPerLocationType() {
			return Math.round(this.totalLocationResults / this.locations.split(',').length);
		},
		a11yStatusClasses() {
			return this.debug ? 'Suggest-debug' : 'is-visuallyHidden';
		},
		poweredByGoogle() {
			return 'data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAJAAAAASCAYAAAC0PldrAAAIHElEQVR4Ae3ZBXDbWB7H8efglpmZGW0HlhzJDpSZmZkZ3W3s2DpmZmbmKx0zM/NdoGhotxTf9x9LHY027paW85v5bBRQopn32weqasqzk5Jw7BE9nHijHo5/Rw/HfqaHYl/keldZ8GJ7qBeqZ/6PNGlPUT5DeVKNqkyc18PJ6VDPtfEfnvUJz0dmpbwfmzMW6k5YFy96pBRTkCnL4MUDiy94oS0F+ZVZlD/5qxLLfNFkz0D0fBtfKOkpCcffrFfG6vVQ4hDUc6ypQLdDFmMdMiWIcjywaOHEB8zynPEFa1pCOWnHLw2Bej5oKtDzqEC+cGyoWZ5YaTDeGer57r4LRPLRCi40liy0QR4eVB5CG2QhU3KQneG+TM/qQhvk424L1Bwt73P22d9QoFD8bVB3KxVUWTX+gqW1mvvLtbr7Z7V+z6frNM/UlFIuKAD83I2v5y69eiL3y9dPZP/s2tdyPn39ZN7UVEq5oCz8x+WvjC/SQrEv8/u/z/O9siSUKNfC8agWSbrRaIH+p3lH1Pg976jxe38gz8JzzLM/g2QhpmIuIjCwB/0cg6HjGAxEsQQtIZmCtbBnLbY4BngZptkGaQmiMHAUhbCyEJMxCxGsh6QFltruOwIv7BmEfTAQwWwsvYMCTcMqGKat6AJJCXYjD1ZcWIdZsEdK8K50gZLroez4+l4G8j1PURlfAJWaMye7Vvd+otbvTVVr7hvVmvc/ci0YyNdAidTHVPa1EzmfuH4iN0WBbnD9H7lu8PXc10CJYDCVxanvffI8nAZvloQTNenZMXYt/YyJ5Y3tgar9nkCt5n2y4Tl073+5TprPEIYSknW2ARpkWosQOkFSiijK0BtuHME2ZGMMDLSDpBMMU29bYaIYBxfW4wBGojcmw8BIWM8Vwjq40QMubMR+231TYGAYJN1QhVUYgEHYgMgdFCiCBeiLYdiJI2iO9og6St4XBgZnKpAWjm2AsmNZO+E8jdlnqzrdvS49aO4fndWLekDVlbmHMoB/MQewAorCrDML86P6E816QD35lbyhzER/SZcouwJKq0oskd9PSX9aVnm5F1RJVbKQr13IVKCU251b7Xf/WwpcqxfMkVnngm9MW+sZ6gJeD24N1BHkwUouDmI28hDGBNjTDwZGIR9hFEOiYyu2YRIkY1GFhzAABvrCnpVYbyvQAeTAyiBHKa2sNUnmY5/jvnwcvYMCbXDMmG1QBR2SJdhu+5l52NXYMsqA7EsXI/5OKBvEhnMKK7LwtePpgYy9HIpB+lbDIGmFbihLTcAzs6FAuueDUJTkW0hdO5XrhrJc/1reTPk6BfsgZMY7iZS/KumFsmihxIFMBaoNeHxmiT/6H5+7Y41esJcl7K/pmdD9N64n4tZALYQzs7EDvTMMmgtBW7GWYyUk26DDj/1wYYHt+z7bUhixMXDMVqDFznG5zX1HIdmDGbDnTvdAGpzZhGWO/2n6ojnCKIIzDNClwQ1LRmU8ETCS3aEysU5r7FHmQckAyUD9sWJgPpSlNlA4SL7O0vJNqGtfz/6bFKX+SyofyvLk1/IGNcxAX8v9JpS8QpDfX/Ha+nwoC8vplIwFYq/TUFbN+w9Zxqr93vpq3fOFWn/BJFlioYQ1UEvgzHxsRQ8Y6NdIgY6jDBI3wugGA53QxVa+IAogeQwR9EB3h27IVGwfqjLc1xWSnZgNZ5bdQYECcGabrcgubMUCPIZjtztQWPsOZpjvPRq52A7KSatKTpV3QfxcXenL61tASUFk8M7q7gIoiywl6UF1fwBKCmLONAVQlhsn8+aYX/8AVEll4pQ8h7x7grLIRj9TgWo0z8Ppsnqv1eqeyP98hX2hZClrrEDH0QpWWuIYJiMbQcx1TNWjYWCQbY8TwQbshJXdWI+obdPdEwbGwJ7ipylQH2vZdN5nK9AMHEVzx1JUeQcF2uVY+rojimJYGY8q7MdEZIy8MGTwfmHuhf5JkTbJex/fyxJdtcpLDzNob5JNrbn/mQMl2ECvNpeKn1sDV6MVjGEz/U9zWSmFYuO82tzr/PzK6fy+UFdP5o6hNP9M742ySyFL1VJrDxSIXu4NJUunvAXPVCApCfufP5p7rg1y2pMZkeswX/u+zIawF0jtwyOmvThiK5UXBhbDgwkIY7mjVKthoBRWymE0MngLbXsrN+bbS5WhQC4sRggVjvtGQ9IWQexGMR7BfjxxBwV6AptRgBIcxU7kwkoODiOCtrhtZOZhkD52m3/KiFGs+VCAdYT3fsicAer5WIcU4DGghPzcta/nfKjhFPa13HrUybW5fBlQQk5hWmXi/fL3rNnuTk5hdX6Pl79/wTwNXrJOYbIXOh9w98atgVqAx7EPx7AUHWDPSGy2la0MOY38zCp0gZWuWNXIbJONAPYhhK0YDSsTocGZbJRmuM9KJ6zAceyHD489zYyxCGMwGYdts24LOLMWi3DHkQ0sg/QqWU5KwvEf4HPMDHseDcc6QTnJUiH7EGajz8g7GPYiH2EWKoOyk/c9vAeax6zzGcryAz5+5PrJ7DIoO37QxTufhRT0C+zLvst11FrC5JQG5f7IrJd5Pjzzx56Pzh4CJc76CnryHugVPMe3OcqfYTkLymkMCpk30RnTlG62jfQLIr5gKkf+wVY29lAWWULTM2HsMah70VSgu888bIULL4hw2pqRPhEmfiMbadmbyWzE/utqSSj2nznBVB7UvWgq0N2lLSIYhxdOWL5k+Xzq/it2mdcGAah7ZV00eQlgxvFRpNfJeyc+Bn2RK32h7sf/AesqcHB02e65AAAAAElFTkSuQmCC';
		},
		listFilter() {
			return this.filter;
		},
		listFilterExclude() {
			return this.filterExclude;
		},
	},
	beforeMount() {
		if (this.url) {
			if (this.url.indexOf('taxon') !== -1 && this.url.indexOf('ebird') !== -1) {
				this.analyticsCategory = 'typeahead_species';
				this.species = true;
			}
			if (this.url.indexOf('taxon') !== -1 && this.url.indexOf('macaulay') !== -1) {
				this.analyticsCategory = 'typeahead_animals';
				this.species = true;
			}
			if (this.url.indexOf('hotspot') !== -1) {
				this.analyticsCategory = 'typeahead_hotspot';
			}
			if (this.url.indexOf('region') !== -1) {
				this.analyticsCategory = 'typeahead_region';
			}
			if (this.url.indexOf('findRegion') !== -1) {
				this.analyticsCategory = 'typeahead_atlas_region';
			}
			if (this.url.indexOf('usfws') !== -1) {
				this.analyticsCategory = 'typeahead_usfws';
			}
			if (this.url.indexOf('user') !== -1) {
				this.analyticsCategory = 'typeahead_user';
			}
		}

		if (this.locations === 'place') {
			this.analyticsCategory = 'typeahead_google_places';
		}

		if (this.localList.length) {
			this.analyticsCategory = 'typeahead_species';
		}
	},
	mounted() {
		this.setLayoutDirection();

		// the following listeners are needed when using this component in a non-Vue context
		document.addEventListener('lichen:resize', this.styleDropdown);
		document.addEventListener('lichen:suggest-filter', this.applyFilterFromEvent);
		document.addEventListener('lichen:suggest-replaceUrlParam', this.replaceUrlParamFromEvent);
		document.addEventListener('lichen:suggest-replaceUrl', this.replaceUrlFromEvent);
		document.addEventListener('lichen:suggest-value', this.setValueFromEvent);
		document.addEventListener('click', this.handleClickToCancel);

		if (this.locations === 'place' || this.locations === 'cities') {
			this.loadGoogleMapsLibrary();
		}
	},
	beforeDestroy() {
		// the following listeners are needed when using this component in a non-Vue context
		document.removeEventListener('lichen:resize', this.styleDropdown);
		document.removeEventListener('lichen:suggest-filter', this.applyFilterFromEvent);
		document.removeEventListener('lichen:suggest-replaceUrlParam', this.replaceUrlParamFromEvent);
		document.removeEventListener('lichen:suggest-replaceUrl', this.replaceUrlFromEvent);
		document.removeEventListener('lichen:suggest-value', this.setValueFromEvent);
		document.removeEventListener('click', this.handleClickToCancel);
	},
	methods: {
		applyFilterFromEvent(e) {
			if (e.detail.name === this.name) {
				if (e.detail.exclude) {
					this.listFilterExclude = e.detail.list;
				} else {
					this.listFilter = e.detail.list;
				}
				if (e.detail.list) {
					this.url = replaceUrlParam(this.url, 'limit', '500');
				} else {
					this.url = replaceUrlParam(this.url, 'limit', '150');
				}
			}
		},
		replaceUrlParamFromEvent(e) {
			if (e.detail.name === this.name) {
				this.url = replaceUrlParam(this.url, e.detail.param, e.detail.value);
			}
		},
		replaceUrlFromEvent(e) {
			if (e.detail.name === this.name) {
				this.url = e.detail.value;
			}
		},
		setValueFromEvent(e) {
			if (e.detail.name === this.name) {
				this.inputValue = e.detail.value;
				this.$forceUpdate();
			}
		},
		handleClickToCancel(e) {
			if (this.open) {
				if (Element.prototype.closest) {
					const el = e.target.closest('.Suggest');
					if (!el) {
						this.open = false;
						this.cancel();
					} else {
						if (el.querySelector('.Suggest-input').getAttribute('id') !== this.id) {
							this.open = false;
							this.cancel();
						}
					}
				}
			}
		},
		handleKeydown(e) {
			if (this.suggestions.length) {
				switch (e.which) {
					case 40:
						e.preventDefault();
						if (this.selectionIndex < this.suggestions.length - 1) {
							this.selectionIndex++;
						} else {
							this.selectionIndex = 0;
						}
						this.inputValue = this.suggestions[this.selectionIndex].name;
						this.status.suggestion = this.suggestions[this.selectionIndex].name;
						break;
					case 38:
						e.preventDefault();
						if (this.selectionIndex > 0) {
							this.selectionIndex--;
						} else {
							this.selectionIndex = this.suggestions.length - 1;
						}
						this.inputValue = this.suggestions[this.selectionIndex].name;
						this.status.suggestion = this.suggestions[this.selectionIndex].name;
						break;
					case 13:
						e.preventDefault();
						this.selected(this.suggestions[this.selectionIndex], this.selectionIndex);
						break;
					case 27:
						if (this.inputValue) {
							e.preventDefault();
							e.stopPropagation();
							this.resetValue();
						}
						break;
					case 9:
						this.open = false;
						this.cancel();
						break;
				}
			} else {
				switch (e.which) {
					case 13:
						if (!this.allowSubmitWithoutSelection) {
							e.preventDefault();
							e.stopPropagation;
						}
						break;
					case 27:
						if (this.inputValue) {
							e.preventDefault();
							e.stopPropagation();
							this.resetValue();
						}
						break;
				}
			}
		},
		handleTyping(e) {
			this.inputValue = e.target.value;
			if (this.inputValue && this.inputValue.length > this.minLength - 1) {
				this.mightCancel = true;
				this.selectionIndex = 0;
				let text = this.inputValue.replace(/\s+/g, ' ');
				if (this.species) {
					text = text.replace(' - ', ' ');
				}
				if (this.locations === 'place' || this.locations === 'cities') {
					this.googleSource(text, (suggestions) => {
						this.suggestions = suggestions;
						this.showSuggestions();
					});
				} else if (this.localList.length) {
					this.searchLocalList(text);
				} else {
					clearTimeout(this.requestTimer);
					let query = this.url.replace('%QUERY', encodeURIComponent(text));
					if (this.cache.hasOwnProperty(query)) {
						this.suggestions = this.cache[query];
						if (this.inputValue && this.inputValue.length > this.minLength - 1) {
							this.showSuggestions();
						}
					} else {
						this.requestTimer = setTimeout(() => {
							this.makeRequest(query);
						}, this.debounceTime);
					}
				}
			} else {
				clearTimeout(this.requestTimer);
				this.suggestions = [];
				this.open = false;
				this.isSearching = false;
				this.mightCancel = true;
			}
		},
		makeRequest(query) {
			this.isSearching = true;
			axios
				.get(query, {
					cancelToken: new axios.CancelToken((c) => {
						if (this.cancelToken) {
							this.cancelToken();
						}
						this.cancelToken = c;
					}),
				})
				.then((res) => {
					if (res.data.length) {
						this.suggestions = res.data;
						this.cache[query] = res.data;
					} else {
						this.suggestions = [];
						this.cache[query] = [];
					}
					if (this.inputValue && this.inputValue.length > this.minLength - 1) {
						this.showSuggestions();
					}
				})
				.catch((err) => {
					this.isSearching = false;
				});
		},
		showSuggestions() {
			if (this.listFilter.length) {
				this.suggestions = this.suggestions.filter((suggestion) => {
					return this.listFilter.indexOf(suggestion.code) !== -1;
				});
			}
			if (this.listFilterExclude.length) {
				this.suggestions = this.suggestions.filter((suggestion) => {
					return this.listFilterExclude.indexOf(suggestion.code) === -1;
				});
			}
			this.suggestions.forEach((suggestion, index) => {
				this.suggestions[index].highlightedName = this.highlight(suggestion.name);
				if (this.species) {
					this.suggestions[index].highlightedName = this.formatSpecies(suggestion.highlightedName);
				}
			});
			this.open = true;
			this.styleDropdown();
			this.status.count = this.suggestions.length;
			if (this.suggestions.length) {
				this.status.suggestion = this.suggestions[this.selectionIndex].name;
			} else {
				this.status.suggestion = '';
			}
			this.isSearching = false;
		},
		resetValue() {
			this.inputValue = '';
			this.status.count = '';
			this.status.suggestion = '';
			this.$refs.input.focus();
			this.suggestions = [];
			this.open = false;
			this.isSearching = false;

			this.$emit('clear');

			this.$refs.input.dispatchEvent(new CustomEvent('suggest:cleared', { bubbles: true }));
		},
		selected(suggestion, index) {
			this.$refs.input.focus();
			this.mightCancel = false;
			this.sendCustomEventToGoogleAnalytics(
				this.analyticsCategory,
				'typeahead_selection',
				'typeahead_query_length',
				this.inputValue.length
			);
			this.sendCustomEventToGoogleAnalytics(this.analyticsCategory, 'typeahead_selection', 'typeahead_suggestion_index', index + 1);
			this.sendCustomEventToGoogleAnalytics(
				this.analyticsCategory,
				'typeahead_selection',
				'typeahead_bucket_' + Math.round((index + 6) / 10),
				this.inputValue.length
			);
			this.inputValue = suggestion.name;
			this.suggestions = [];
			this.selectionIndex = index;
			(this.status.count = ''), (this.status.suggestion = '');
			this.open = false;

			if (this.locations === 'place' || this.locations === 'cities') {
				var request = {
					placeId: suggestion.code,
					fields: ['name', 'geometry'],
				};
				var service = new google.maps.places.PlacesService(document.createElement('div'));
				service.getDetails(request, (place, status) => {
					if (status == google.maps.places.PlacesServiceStatus.OK) {
						suggestion.place = place;
						this.$emit('selection', suggestion);
						if (this.clearOnSelect) {
							this.inputValue = '';
						}
					}
				});
			} else {
				this.$emit('selection', suggestion);
				this.$refs.input.dispatchEvent(
					new CustomEvent('suggest:selected', {
						bubbles: true,
						detail: suggestion,
					})
				);
				if (this.goTo) {
					window.location.href = this.goTo.replace('%CODE', suggestion.code).replace('%NAME', suggestion.name);
				}
				if (this.clearOnSelect) {
					this.inputValue = '';
				}
			}
		},
		styleDropdown() {
			if (this.$refs.input) {
				const inputOffset = this.$refs.input.getBoundingClientRect();
				let maxHeight = window.innerHeight - inputOffset.height - inputOffset.top - 8;
				if (maxHeight < 96) {
					maxHeight = 96;
				}
				this.dropdownMaxHeight = maxHeight + 'px';
			}
		},
		googleSource(query, callback) {
			this.isSearching = true;
			const displaySuggestions = (suggestions, status) => {
				this.isSearching = false;
				if (status != google.maps.places.PlacesServiceStatus.OK) {
					return;
				}
				suggestions = suggestions.map((suggestion) => {
					return {
						name: suggestion['description'],
						code: suggestion['place_id'],
						data: suggestion,
					};
				});
				callback(suggestions);
			};
			if (query.length) {
				let service = new google.maps.places.AutocompleteService();
				let config = {};
				if (this.locations === 'cities') {
					config = {
						input: query,
						types: ['(cities)'],
					};
				} else {
					config = {
						input: query,
					};
				}
				service.getPlacePredictions(config, displaySuggestions);
			}
		},
		searchLocalList(text) {
			this.suggestions = this.localList.filter((item) => {
				if (item.hasOwnProperty('nameToMatch')) {
					return item.nameToMatch.toLowerCase().indexOf(text.toLowerCase()) !== -1;
				} else {
					return item.name.toLowerCase().indexOf(text.toLowerCase()) !== -1;
				}
			});
			this.showSuggestions();
		},
		sortSuggestions(suggestions, query) {
			const regExp = new RegExp(query, 'gi');
			suggestions.forEach(function (x) {
				x.relevance = (x.name.match(regExp) || []).length;
				x.charLength = x.name.length;
			});
			suggestions.sort(function (a, b) {
				if (a.relevance === b.relevance) {
					return a.charLength - b.charLength;
				}
				return b.relevance - a.relevance;
			});
			return suggestions;
		},
		formatSpecies(text) {
			const parts = text.split(' - ');
			if (parts[1]) {
				return parts[0] + ' <span class="SciName">' + parts[1] + '</span>';
			} else {
				return text;
			}
		},
		highlight(text) {
			const queryParts = this.inputValue.split(/\s+/);
			let looseQuery = queryParts
				.filter((s) => {
					return !!s;
				})
				.map((s) => {
					return s.split('').map(this.regexEscape).join("[-'()/\\[\\]]?");
				})
				.join('|');
			const regExp = new RegExp('(' + looseQuery + ')', 'gi');
			return text.replace(regExp, '<em>$1</em>');
		},
		cancel() {
			if (this.mightCancel) {
				this.open = false;
				this.mightCancel = false;
				this.suggestions = [];
				this.status.count = '';
				this.status.suggestion = '';
				this.sendCustomEventToGoogleAnalytics(
					this.analyticsCategory,
					'typeahead_cancellation',
					'typeahead_query_length',
					this.inputValue.length
				);
			}
		},
		loadGoogleMapsLibrary() {
			if (!window.googleMapsApiLoaded) {
				window.googleMapsApiLoaded = () => {
					if (typeof google == 'undefined') {
						this.googleMapsApiLoaded;
						const script_tag = document.createElement('script');
						script_tag.setAttribute('type', 'text/javascript');
						script_tag.setAttribute(
							'src',
							'https://maps.googleapis.com/maps/api/js?key=' +
								this.googleApiKey +
								'&libraries=places&callback=googleMapsApiLoaded'
						);
						(document.getElementsByTagName('head')[0] || document.documentElement).appendChild(script_tag);
					} else {
						this.googleMapsApiLoaded;
						document.dispatchEvent(new CustomEvent('lichen:googleMapsApiLoaded'));
					}
				};
				window.googleMapsApiLoaded();
			} else {
				this.googleMapsApiLoaded = true;
			}
		},
		sendCustomEventToGoogleAnalytics(category, action, label, value) {
			try {
				if (typeof ga === 'function') {
					if (typeof ga.getAll === 'function') {
						var tracker = ga.getAll()[0];
						if (tracker) {
							if (typeof value != 'undefined') {
								tracker.send('event', category, action, label, value);
							} else {
								tracker.send('event', category, action, label);
							}
						}
					}
				}
			} catch (error) {}
		},
		setLayoutDirection() {
			const body = document.querySelector('body');
			if (body.getAttribute('dir') === 'rtl') {
				this.layoutDirection = 'rtl';
			}
		},
	},
};
</script>

<style lang="scss">
.Suggest {
	@include stack-md;
	.Suggest-container {
		position: relative;
		display: flex;
		background-color: $color-white;
		border: 1px solid $color-form-outline;
		border-radius: $radius-sm;
		&.isFocused {
			outline: 2px solid $color-form-outline-focus;
		}
		.Suggest-icon,
		.Suggest-spinner,
		.Suggest-reset {
			@include inset-sm;
			display: flex;
			align-items: center;
			line-height: 1;
			&:last-child {
				margin: 0;
			}
			> .Icon,
			.Icons--search,
			.Icon--close {
				@include icon-sm;
			}
		}
	}
	.Suggest-inputContainer {
		flex: 1 1 auto;
		padding-left: 0 !important;
		padding-right: 0 !important;
	}
	.Suggest-input {
		@include text(4);
		text-align: left;
		padding: $space-sm $space-sm $space-sm 0;
		[dir='rtl'] & {
			padding: $space-sm 0 $space-sm $space-sm;
			text-align: right;
		}
		border: 0;
		box-shadow: none;
		outline: 0;
		margin: 0;
		height: auto;
		width: 100%;
		color: $color-text;
		background-color: transparent;
	}
	// this doesn't seem to work, but should get rid of the extra X in Edge and IE
	// might have to do with compatibility mode?
	.Suggest-input::ms-clear {
		display: none;
		width: 0;
		height: 0;
	}
	.Suggest-icon,
	.Suggest-spinner,
	.Suggest-reset {
		color: $color-icon-muted;
	}
	.Suggest-spinner .Icon {
		animation-duration: 1.2s;
		animation-name: spin;
		animation-iteration-count: infinite;
		animation-timing-function: linear;
	}

	/*
	* dropdown and suggestions
	*
	*/
	.Suggest-dropdown {
		position: absolute;
		background-color: $color-white;
		overflow-y: auto;
		border: 1px solid $color-neutral-3;
		border-radius: $radius-sm;
		right: -1px; // account for border width
		left: -1px; // account for border width
		top: 100%;
		width: 100%;
		z-index: 100; // maybe should be higher?
		@include dropShadow(2);
		.Suggest-suggestions {
			.Suggest-suggestion {
				display: flex;
				align-items: center;
				@include text(3);
				color: $color-text;
				cursor: pointer;
				@include inset-sm;
				&.Suggest-cursor,
				&:hover {
					background-color: $color-primary-1;
				}
				em {
					font-weight: bold;
					font-style: normal;
				}
				.Suggest-suggestionLabel {
					flex: 0 0 auto;
					margin-right: $space-sm;
					text-align: center;
					min-width: 3rem;
					.Icon {
						@include icon-sm;
						fill: $color-neutral-3;
					}
					span {
						display: block;
						font-size: 8px;
						text-transform: uppercase;
						color: $color-neutral-4;
					}
				}
			}
		}
		.Suggest-empty {
			@include inset-xs;
			color: $color-text;
		}
		.Suggest-emptyCreate {
			font-weight: bold;
		}
		// powered by Google
		.Suggest-datasetFooter {
			@include inline-sm;
			text-align: right;
			img {
				height: 12px;
			}
		}
	}

	.SciName {
		color: $color-neutral-4;
		@include text(2);
		font-style: italic;
	}

	// label
	.Suggest-label {
		@include text(3);
		@include stack-xs;
	}

	// submit buttons
	.Suggest-submit {
		padding: 0 !important;
		align-items: stretch;
		.Button {
			margin: 0;
			border-radius: 0;
			border-top-right-radius: $radius-sm;
			border-bottom-right-radius: $radius-sm;
			[dir='rtl'] & {
				border-top-left-radius: $radius-sm;
				border-bottom-left-radius: $radius-sm;
				border-top-right-radius: 0;
				border-bottom-right-radius: 0;
			}
		}
	}

	// a11y status, when debug=true
	.Suggest-debug {
		@include text(1);
		position: fixed;
		bottom: 0;
		z-index: 1000;
		background-color: $color-overlay-white;
	}
}

/*
* Size variants
*
*/

.Suggest--lg {
	.Suggest-container {
		.Suggest-icon,
		.Suggest-spinner,
		.Suggest-reset {
			@include inset-md;
			> .Icon,
			.Icon--search,
			.Icon--close {
				@include icon-sm;
			}
		}
	}
	.Suggest-input {
		@include text(5);
		padding: $space-md $space-md $space-md 0;
		[dir='rtl'] & {
			padding: $space-md 0 $space-md $space-md;
		}
	}
}

.Suggest--sm {
	@include stack-sm;
	.Suggest-container {
		.Suggest-icon,
		.Suggest-spinner,
		.Suggest-reset,
		.Suggest-input {
			@include inset-xs;
			> .Icon,
			.Icon--search,
			.Icon--close {
				@include icon-sm;
			}
		}
	}
	.Suggest-input {
		@include text(3);
		padding: $space-xs $space-xs $space-xs 0;
		[dir='rtl'] & {
			padding: $space-xs 0 $space-xs $space-xs;
		}
	}
	.Suggest-label {
		@include text(2);
	}
}

/*
* Style variants
*
*/

.Suggest--pill {
	.Suggest-container {
		border-radius: 999px;
	}
	.Suggest-wrapper {
		.Suggest-dropdown {
			border-radius: $radius-sm;
		}
	}
	.Suggest-icon {
		margin-left: $space-xs;
		[dir='rtl'] & {
			margin-left: 0;
			margin-right: $space-xs;
		}
	}
	.Suggest-reset {
		margin-right: $space-xs;
		[dir='rtl'] & {
			margin-right: 0;
			margin-left: $space-xs;
		}
	}
	.Suggest-submit {
		.Button {
			border-top-right-radius: 999px;
			border-bottom-right-radius: 999px;
			[dir='rtl'] & {
				border-top-left-radius: 999px;
				border-bottom-left-radius: 999px;
				border-top-right-radius: 0;
				border-bottom-right-radius: 0;
			}
		}
	}
}

.Suggest--hideLabel {
	.Suggest-label {
		@include visuallyHidden;
	}
}

.Suggest--noBorder {
	.Suggest-container {
		border: 0;
		&.isFocused {
			border: 0;
			box-shadow: 0 0 0 1px $color-form-outline-focus;
		}
	}
}

.Suggest--whiteBorder {
	.Suggest-container {
		border: 1px solid $color-white;
	}
}

.Suggest--submitNeutral {
	.Suggest-submit {
		.Button {
			background-color: $color-neutral-2;
			color: $color-neutral-5;
		}
	}
}

.Suggest--reverse {
	.Suggest-container {
		background-color: $color-black;
		border-color: $color-form-outline-reverse;
		&.isFocused {
			outline: 2px solid $color-form-outline-focus-reverse;
		}
	}
	.Suggest-input {
		color: $color-text-reverse;
	}
	.Suggest-icon,
	.Suggest-spinner,
	.Suggest-reset {
		color: $color-icon-muted-reverse;
	}
	.Suggest-label {
		color: $color-text-reverse;
	}
}
</style>
